import React, { useEffect, useRef } from 'react';
import { Portal } from 'react-portal';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';
import { theme, toRem } from 'design';
import { ModalProps } from './ModalProps';

// Locks the page's own scrollbar while a modal is mounted so only the modal scrolls.
const BodyScrollLock = createGlobalStyle`
  body { overflow: hidden; }
`;

const Layout = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${theme.zIndex.modal};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 ${toRem(theme.space.xl)};
  background-color: rgba(83, 92, 112, 0.88);
  overflow: auto;
`;

interface IModalContainerProps extends ModalProps {
  className?: string;
  closeOnOutSideClick?: boolean;
}

export const ModalContainer: React.FC<IModalContainerProps> = ({ isActive, closeOnOutSideClick = true, onClose, children, className, dataCy = 'modal' }) => {
  const mouseDownOnBackdrop = useRef(false);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // A press on the container's own scrollbar also targets it but reports a clientX beyond clientWidth
    mouseDownOnBackdrop.current = event.target === event.currentTarget && event.clientX < event.currentTarget.clientWidth;
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (mouseDownOnBackdrop.current && event.target === event.currentTarget) {
      onClose?.();
    }
    mouseDownOnBackdrop.current = false;
  };

  useEffect(() => {
    if (!isActive || !closeOnOutSideClick || !onClose) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, closeOnOutSideClick, onClose]);

  return (
    <Portal>
      <AnimatePresence>
        {isActive && (
          <Layout
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ ease: 'easeInOut', duration: 0.2 }}
            className={className}
            data-cy={dataCy}
            {...(closeOnOutSideClick && {
              onMouseDown: handleMouseDown,
              onMouseUp: handleMouseUp,
            })}
          >
            <BodyScrollLock />
            {children}
          </Layout>
        )}
      </AnimatePresence>
    </Portal>
  );
};
